import axios from 'axios';
import download from 'downloadjs';
import { openNotification } from '../helpers/notification';

export const baseURL = process.env.REACT_APP_API_URL;
export const TELERIK_BASE_URL = process.env.REACT_APP_TELERIK_URL;
export const TELERIK_API_BASE_URL = process.env.REACT_APP_TELERIK_BASE_URL;

export const getToken = () => {
  const serializedState = localStorage.getItem('IEMS');
  const auth = JSON.parse(serializedState);
  return auth.auth.token;
};

const getAiReportHeaders = () => ({
  Authorization: `bearer ${getToken()}`,
  'Content-Type': 'application/json',
});

export const getAiReportModels = async () => {
  const response = await axios.get(`${baseURL}/AiReport/Models`, {
    headers: getAiReportHeaders(),
  });
  const payload = response.data;
  const status = payload?.status || payload?.result?.status;
  if (status?.code && status.code !== '00') {
    throw new Error(status.desc || 'Unable to load AI models');
  }
  return Array.isArray(payload)
    ? payload
    : payload?.data || payload?.result?.data || [];
};

export const generateAiReport = async (request) => {
  const response = await axios.post(`${baseURL}/AiReport`, request, {
    headers: getAiReportHeaders(),
  });
  const payload = response.data;
  const status = payload?.status || payload?.result?.status;
  if (status?.code && status.code !== '00') {
    throw new Error(status.desc || 'Unable to generate the report');
  }
  return payload?.data || payload?.result?.data || payload;
};

const requestAiSettings = async (method, path, data) => {
  const response = await axios({
    method,
    url: `${baseURL}${path}`,
    headers: getAiReportHeaders(),
    ...(data === undefined ? {} : { data }),
  });
  const payload = response.data;
  const status = payload?.status || payload?.result?.status;
  if (status?.code && status.code !== '00') {
    throw new Error(status.desc || 'Unable to update AI model settings');
  }
  return payload?.data || payload?.result?.data || payload;
};

export const getAiSettings = () => requestAiSettings('get', '/AiSettings');

export const createAiSetting = (model) => requestAiSettings('post', '/AiSettings', model);

export const updateAiSetting = (model) => requestAiSettings('put', '/AiSettings', model);

export const setDefaultAiSetting = (id) =>
  requestAiSettings('put', `/AiSettings/${encodeURIComponent(id)}/Default`);

export const deleteAiSetting = (id) =>
  requestAiSettings('delete', `/AiSettings/${encodeURIComponent(id)}`);

// ---- Generated facility reports (built automatically when a report is published) ----

const unwrapGeneratedReports = (payload, fallbackMessage) => {
  const status = payload?.status || payload?.result?.status;
  // "03" = empty list
  if (status?.code && !['00', '03'].includes(status.code)) {
    throw new Error(status.desc || fallbackMessage);
  }
  return payload?.result ?? payload;
};

export const REPORT_ACCESS_DENIED_MESSAGE =
  "You don't have permission to view these reports. Ask an administrator to give your role the View Report permission.";

const isAccessDeniedStatus = (status) => status === 401 || status === 403;

// Turn an axios error from the report APIs into an Error with a readable message.
// 401/403 means the role lacks the needed permission (or the session expired).
const toReportApiError = (error, fallbackMessage) => {
  const status = error?.response?.status;
  const body = error?.response?.data;
  const serverMessage = body?.status?.desc || body?.result?.status?.desc || body?.message;
  const wrapped = new Error(
    serverMessage ||
      (isAccessDeniedStatus(status) ? REPORT_ACCESS_DENIED_MESSAGE : '') ||
      (error?.response ? fallbackMessage : error?.message) ||
      fallbackMessage
  );
  wrapped.status = status;
  wrapped.accessDenied = isAccessDeniedStatus(status);
  return wrapped;
};

const toGeneratedReportError = async (response, fallbackMessage) => {
  let message = fallbackMessage;
  try {
    const body = await response.json();
    message = body?.status?.desc || body?.result?.status?.desc || body?.message || message;
  } catch (e) {
    // body was not JSON
  }
  if (response.status === 404) message = 'Report not found';
  if (isAccessDeniedStatus(response.status) && message === fallbackMessage) {
    message = REPORT_ACCESS_DENIED_MESSAGE;
  }
  const error = new Error(message);
  error.status = response.status;
  error.accessDenied = isAccessDeniedStatus(response.status);
  return error;
};

export const getGeneratedReports = async ({
  facilityId = '',
  year = '',
  quarter = '',
  reportType = '',
  offset = 0,
  limit = 50,
} = {}) => {
  const params = new URLSearchParams({ facilityId, year, quarter, reportType, offset, limit });
  let response;
  try {
    response = await axios.get(`${baseURL}/GeneratedReports?${params.toString()}`, {
      headers: getAiReportHeaders(),
    });
  } catch (error) {
    throw toReportApiError(error, 'Unable to load generated reports');
  }
  const result = unwrapGeneratedReports(response.data, 'Unable to load generated reports');
  const list = Array.isArray(result?.data)
    ? result.data
    : Array.isArray(result?.data?.data)
    ? result.data.data
    : [];
  return { data: list, total: result?.total ?? result?.data?.total ?? list.length };
};

export const getGeneratedReportVersions = async (reportId) => {
  let response;
  try {
    response = await axios.get(
      `${baseURL}/GeneratedReports/ByReport/${encodeURIComponent(reportId)}`,
      { headers: getAiReportHeaders() }
    );
  } catch (error) {
    throw toReportApiError(error, 'Unable to load report versions');
  }
  const result = unwrapGeneratedReports(response.data, 'Unable to load report versions');
  return Array.isArray(result?.data) ? result.data : Array.isArray(result) ? result : [];
};

// The HTML endpoints need the bearer token, so pages are fetched as text and rendered via srcDoc
const fetchReportHtml = async (path) => {
  const response = await fetch(`${baseURL}${path}`, {
    headers: { Authorization: `bearer ${getToken()}` },
  });
  if (!response.ok) throw await toGeneratedReportError(response, 'Unable to load the report');
  return response.text();
};

const downloadReportPdf = async (path, fallbackFileName) => {
  const response = await fetch(`${baseURL}${path}`, {
    headers: { Authorization: `bearer ${getToken()}` },
  });
  if (!response.ok) throw await toGeneratedReportError(response, 'Unable to download the PDF');
  const blob = await response.blob();
  const fileName =
    /filename="?([^";]+)"?/.exec(response.headers.get('Content-Disposition') || '')?.[1] ||
    fallbackFileName;
  download(blob, fileName, 'application/pdf');
};

// ---- AI access and daily limits ----

const requestAiAccess = async (method, path, fallbackMessage, data) => {
  try {
    const response = await axios({
      method,
      url: `${baseURL}/AiAccess${path}`,
      headers: getAiReportHeaders(),
      ...(data === undefined ? {} : { data }),
    });
    return unwrapGeneratedReports(response.data, fallbackMessage);
  } catch (error) {
    const body = error?.response?.data;
    const message =
      body?.status?.desc || body?.result?.status?.desc || body?.message || error.message;
    const wrapped = new Error(message || fallbackMessage);
    wrapped.status = error?.response?.status;
    throw wrapped;
  }
};

const toQuery = (params) =>
  new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  ).toString();

// Signed-in user's AI access, one status per feature: { assistant, aiReport }.
// Each: { allowed, reason, reasonCode, dailyLimit, usedToday, remaining, totalDailyLimit,
// totalRemaining, facilities (consultants/personnel, Ask AI), selectedFacilityId, resetsAtUtc }
export const getAiAccessMe = async () => {
  const result = await requestAiAccess('get', '/Me', 'Unable to check AI access');
  return result?.data ?? result;
};

// Admin (MANAGE_AI_SETTINGS)
export const getAiAccessSettings = async ({
  scope,
  search,
  userCategory,
  feature,
  offset = 0,
  limit = 50,
}) => {
  const result = await requestAiAccess(
    'get',
    `/Settings?${toQuery({ scope, search, userCategory, feature, offset, limit })}`,
    'Unable to load AI access settings'
  );
  const list = Array.isArray(result?.data) ? result.data : [];
  // total is null when the server doesn't send one (the list is paged on the server)
  const total = result?.total ?? result?.totalCount ?? result?.data?.total ?? null;
  return { data: list, total };
};

// body: { scope, ids?, all?, userCategory?, enabled?, dailyLimit?, clearDailyLimit? }; returns items changed
export const updateAiAccessSettings = async (body) => {
  const result = await requestAiAccess('put', '/Settings', 'Unable to save AI access settings', body);
  return result?.data ?? result;
};

// body: { scope, ids? } or { scope, all: true }; returns settings removed
export const resetAiAccessSettings = async (body) => {
  const result = await requestAiAccess(
    'post',
    '/Settings/Reset',
    'Unable to reset AI access settings',
    body
  );
  return result?.data ?? result;
};

// Ask AI questions per facility per day: { facilityId, facilityName, usageDate, questions, users }
export const getAiAccessFacilityUsage = async ({ from, to, facilityId } = {}) => {
  const result = await requestAiAccess(
    'get',
    `/Usage/Facilities?${toQuery({ from, to, facilityId })}`,
    'Unable to load AI usage by facility'
  );
  return Array.isArray(result?.data) ? result.data : [];
};

export const getAiAccessUsage = async ({ from, to, userCategory, userId } = {}) => {
  const result = await requestAiAccess(
    'get',
    `/Usage?${toQuery({ from, to, userCategory, userId })}`,
    'Unable to load AI usage'
  );
  return Array.isArray(result?.data) ? result.data : [];
};

export const getGeneratedReportHtml = (id) =>
  fetchReportHtml(`/GeneratedReports/${encodeURIComponent(id)}/Html`);

export const downloadGeneratedReportPdf = (id, fallbackFileName = 'facility-report.pdf') =>
  downloadReportPdf(`/GeneratedReports/${encodeURIComponent(id)}/Pdf`, fallbackFileName);

export const regenerateGeneratedReport = async (reportId) => {
  let response;
  try {
    response = await axios.post(
      `${baseURL}/GeneratedReports/Regenerate/${encodeURIComponent(reportId)}`,
      null,
      { headers: getAiReportHeaders() }
    );
  } catch (error) {
    throw toReportApiError(error, 'Unable to regenerate the report');
  }
  const result = unwrapGeneratedReports(response.data, 'Unable to regenerate the report');
  return result?.data ?? result;
};

// ---- AI skill reports (tailored, AI-written versions of a published report; supplementary only) ----

const requestAiSkillReports = async (method, path, fallbackMessage, data) => {
  let response;
  try {
    response = await axios({
      method,
      url: `${baseURL}/AiSkillReports${path}`,
      headers: getAiReportHeaders(),
      ...(data === undefined ? {} : { data }),
    });
  } catch (error) {
    throw toReportApiError(error, fallbackMessage);
  }
  return unwrapGeneratedReports(response.data, fallbackMessage);
};

// IEMS admin contact email(s), e.g. for "your facility is not provisioned for AI Reporting".
// Accepts a string (comma/semicolon separated), an array of strings or { email } objects, or a
// settings object such as { value: "a@x.ng, b@y.ng" }; always returns a de-duplicated array.
export const getIemsContactEmails = async () => {
  const response = await axios.get(`${baseURL}/Setting/IemsContactEmail`, {
    headers: { Authorization: `bearer ${getToken()}` },
  });
  const payload = response.data;
  const data = payload?.result?.data ?? payload?.data ?? payload;
  const EMAIL = /[^\s,;<>"']+@[^\s,;<>"']+\.[^\s,;<>"']+/g;

  const collect = (value) => {
    if (!value) return [];
    if (typeof value === 'string') return value.match(EMAIL) || [];
    if (Array.isArray(value)) return value.flatMap(collect);
    if (typeof value === 'object') {
      return collect(
        value.email ??
          value.emailAddress ??
          value.contactEmail ??
          value.iemsContactEmail ??
          value.value ??
          value.settingValue ??
          ''
      );
    }
    return [];
  };

  return [...new Set(collect(data).map((e) => e.trim()))];
};

// facilityId: the facility being viewed, so the server can say whether AI reports are
// enabled for that facility (ignored by servers that don't support it yet)
export const getAiSkillReportAvailability = async (facilityId) => {
  const result = await requestAiSkillReports(
    'get',
    `/Availability${facilityId ? `?facilityId=${encodeURIComponent(facilityId)}` : ''}`,
    'Unable to check AI report availability'
  );
  console.log("AI report availability result:", result);
  return result?.data ?? result;
};

export const getAiSkillReports = async ({
  facilityId = '',
  reportId = '',
  year = '',
  quarter = '',
  offset = 0,
  limit = 50,
} = {}) => {
  const params = new URLSearchParams({ facilityId, reportId, year, quarter, offset, limit });
  const result = await requestAiSkillReports(
    'get',
    `?${params.toString()}`,
    'Unable to load AI reports'
  );
  const list = Array.isArray(result?.data)
    ? result.data
    : Array.isArray(result?.data?.data)
    ? result.data.data
    : [];
  return { data: list, total: result?.total ?? result?.data?.total ?? list.length };
};

export const requestAiSkillReport = async ({ reportId, audience, instructions }) => {
  const result = await requestAiSkillReports('post', '', 'Unable to request the AI report', {
    reportId,
    ...(audience ? { audience } : {}),
    ...(instructions ? { instructions } : {}),
  });
  return result?.data ?? result;
};

export const getAiSkillReportHtml = (id) =>
  fetchReportHtml(`/AiSkillReports/${encodeURIComponent(id)}/Html`);

export const downloadAiSkillReportPdf = (id, fallbackFileName = 'ai-facility-report.pdf') =>
  downloadReportPdf(`/AiSkillReports/${encodeURIComponent(id)}/Pdf`, fallbackFileName);

export const makeApiCall = (axiosConfigObj) => {
  const config = {
    ...axiosConfigObj,
    baseURL,
  };

  return new Promise((resolve, reject) => {
    return axios(config)
      .then(({ data }) => {
        if (data.result.status.code !== '00') return reject(data);
        return resolve(data);
      })
      .catch((err) => {
        const { response, request, message } = err;
        let error = response
          ? response.data.message
          : request
          ? 'Network error, please try again later'
          : message;
        if (response) {
          if (!response.data.message) {
            error = response;
          }
        }
        return reject(error);
      });
  });
};

export const downloadCertificate = async (ref) => {
  const url = `/Payment/DownloadCertificate/${ref}`;
  try {
    openNotification({
      type: 'info',
      title: 'Download started...',
      message: 'Your certificate is being downloaded in the background',
      duration: 10,
    });
    const response = await axios(url, { responseType: 'blob' });
    if (response.data.statusCode === 404) {
      throw new Error('No certificate found for the selected report');
    }
    return download(response.data, 'certificate.pdf', 'application/pdf');
  } catch (error) {
    return openNotification({
      type: 'error',
      title: 'File download failed',
      message: error.message,
    });
  }
};

export const getReports2 = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const reportList = await makeApiCall({
        url: `/Report`,
        method: 'get',
        headers: { Authorization: `bearer ${getToken()}` },
      });
      return resolve(reportList);
    } catch (error) {
      return reject('Error fetching list of reports');
    }
  });
};

export const getFacilities = (params) => {
  let url = '/Facility?Start=0&Stop=100000';
  if (params) url = `/Facility${params}`;

  return new Promise(async (resolve, reject) => {
    try {
      const facilities = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(facilities);
    } catch (error) {
      if (error && error.result && error.result.status && error.result.status.desc === 'EMPTY')
        return resolve(error);
      else if (
        error &&
        error.result &&
        error.result.status &&
        error.result.status.desc === 'NOT FOUND'
      )
        return resolve(null);
      else if (!error) {
        return resolve();
      } else reject('Error fetching list of facilities');
    }
  });
};

export const getFacilitiyV2 = (
  search,
  facilityStatus,
  eiaStatus,
  zoneId,
  stateId,
  sectorId,
  lgaId,
  categoryId,
  start,
  stop
) => {
  let url = `/Facility/V2?search=${search}&facilityStatus=${facilityStatus}&eiaStatus=${eiaStatus}&zoneId=${zoneId}&stateId=${stateId}&sectorId=${sectorId}&lgaId=${lgaId}&categoryId=${categoryId}&start=${start}&stop=${stop}`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getFacilitiyCategory = () => {
  let url = `/Facility/FacilityCategory`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getFacilitiyByIdV2 = (facilityId) => {
  let url = `/Facility/V2/${facilityId}`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const addFacilityPersonnel = (facilityId, data) => {
  let url = `${baseURL}/Facility/${facilityId}/AddPersonnel`;

  return new Promise(async (resolve, reject) => {
    try {
      //const fetch = axios.get(url);
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'post',
        data: data,
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const removeFacilityPersonnel = (facilityId, data) => {
  let url = `${baseURL}/Facility/${facilityId}/RemovePersonnel`;

  return new Promise(async (resolve, reject) => {
    try {
      //const fetch = axios.get(url);
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'delete',
        data: data,
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const makeFacilityPersonnelAdministrator = (data) => {
  let url = `${baseURL}/Facility/MakePersonnelAdministrator`;

  return new Promise(async (resolve, reject) => {
    try {
      //const fetch = axios.get(url);
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'post',
        data: data,
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const updateFacilityPersonnel = (facilityId, data) => {
  let url = `${baseURL}/Facility/${facilityId}/UpdatePersonnel`;

  return new Promise(async (resolve, reject) => {
    try {
      //const fetch = axios.get(url);
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'post',
        data: data,
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getFacilityByUserId = (userId) => {
  const url = `${baseURL}/TelerikReports/GetFacilityList/${userId}`;
  const fetch = axios.get(url);
  return fetch;
};

export const getFacilityAll = () => {
  const url = `${baseURL}/TelerikReports/GetFacilities`;
  const fetch = axios.get(url);
  return fetch;
};

// export const getFacilityByUserId = (userId) => {
//   return new Promise(async (resolve, reject) => {
//     try {
//       const url = `/TelerikReports/GetFacilityList/${userId}`;
//       console.log('url', url);
//       const facility = await makeApiCall({
//         url,
//         //headers: { Authorization: `bearer ${getToken()}` },
//         method: 'get',
//       });
//       return resolve(facility);
//     } catch (error) {
//       return reject('Error fetching facility');
//     }
//   });
// };

export const getFacilityAdhocReport = (facilityId, reportId) => {
  let url = `/Facility/${facilityId}/SamplePoints?ReportID=${reportId}`;

  return new Promise(async (resolve, reject) => {
    try {
      const adhocReport = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(adhocReport);
    } catch (error) {
      reject(error);
    }
  });
};

export const getReports = (params) => {
  let url = `/Report?limit=100000`;
  if (params) url = `/Report${params}`;
  return new Promise(async (resolve, reject) => {
    try {
      const reports = await makeApiCall({
        url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(reports);
    } catch (error) {
      if (error && error.result && error.result.status && error.result.status.desc === 'EMPTY')
        return resolve(error);
      return reject(error);
    }
  });
};

export const getReportsV2 = (
  search,
  facilityId,
  stateId,
  reportStatus,
  reportYear,
  reportType,
  planningStatus,
  reportQuarter,
  start,
  stop
) => {
  //let url = `/Report`

  let url = `/Report/V2?search=${search}&facilityId=${facilityId}&stateId=${stateId}&reportStatus=${reportStatus}&reportYear=${reportYear}&reportType=${reportType}&planningStatus=${planningStatus}&start=${start}&stop=${stop}`;

  if (reportQuarter) {
    url = `/Report/V2?search=${search}&facilityId=${facilityId}&stateId=${stateId}&reportStatus=${reportStatus}&reportYear=${reportYear}&reportType=${reportType}&planningStatus=${planningStatus}&reportQuarter=${reportQuarter}&start=${start}&stop=${stop}`;
  }
  return new Promise(async (resolve, reject) => {
    try {
      const reports = await makeApiCall({
        url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(reports);
    } catch (error) {
      if (error && error.result && error.result.status && error.result.status.desc === 'EMPTY')
        return resolve(error);
      return reject(error);
    }
  });
};

export const getIcons = () => {
  let url = `/Icon`;
  return new Promise(async (resolve, reject) => {
    try {
      const icons = await makeApiCall({
        url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(icons);
    } catch (error) {
      if (error && error.result && error.result.status && error.result.status.desc === 'EMPTY')
        return resolve(error);
      return reject(error);
    }
  });
};

export const getFacility = (facilityId, year = new Date().getFullYear()) => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = `/Facility/${facilityId}/FacilityReport?year=${year}`;
      const facility = await makeApiCall({
        url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(facility);
    } catch (error) {
      return reject('Error fetching facility');
    }
  });
};

export const getFacilityReport = (facilityId, qtr, year = new Date().getFullYear()) => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = `/Facility/${facilityId}/QuarterReport/${qtr}?year=${year}`;
      let facilityReport = null;
      facilityReport = await makeApiCall({
        url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(facilityReport);
    } catch (error) {
      return reject('Error fetching facility report');
    }
  });
};

export const getAdhocReport = (facilityId, year = new Date().getFullYear()) => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = `/Facility/${facilityId}/FacilityReport?adhoc=true&year=${year}`;
      let facilityReport = null;
      facilityReport = await makeApiCall({
        url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(facilityReport);
    } catch (error) {
      if (error && error.result && error.result.status && error.result.status.desc === 'NOT FOUND')
        return resolve(error);
      return reject('Error fetching facility report');
    }
  });
};

export const getComparativeReport = (facilityId, year) => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = `/Facility/${facilityId}/ComparativeReport?year=${year}`;
      let comparativeReport = null;
      comparativeReport = await makeApiCall({
        url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(comparativeReport);
    } catch (error) {
      return reject('Error fetching facility report');
    }
  });
};

export const getReportById = (reportId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = `/Report/${reportId}`;
      let report = null;
      report = await makeApiCall({
        url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(report);
    } catch (error) {
      return reject('Error fetching facility report');
    }
  });
};

export const submitReport = (reportId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = `/Report/${reportId}/SubmitReport`;
      let sptRecord = null;
      sptRecord = await makeApiCall({
        url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'patch',
        //data,
      });
      return resolve(sptRecord);
    } catch (error) {
      return reject(error);
    }
  });
};

export const GetSubmittedReportCount = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = `/Report/GetSubmittedReportCount`;
      let sptRecord = null;
      sptRecord = await makeApiCall({
        url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
        //data,
      });
      return resolve(sptRecord);
    } catch (error) {
      return reject(error);
    }
  });
};

export const GetRejectedReportCount = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = `/Report/GetRejectedReportCount`;
      let sptRecord = null;
      sptRecord = await makeApiCall({
        url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
        //data,
      });
      return resolve(sptRecord);
    } catch (error) {
      return reject(error);
    }
  });
};

export const getSamplePointRecord = (samplePointId, reportId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = `/Report/${reportId}/SamplePoint/${samplePointId}`;
      let sptRecord = null;
      sptRecord = await makeApiCall({
        url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(sptRecord);
    } catch (error) {
      return reject(error);
    }
  });
};

export const sendSamplePointRecord = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = `/SamplePointResult`;
      let sptRecord = null;
      sptRecord = await makeApiCall({
        url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'post',
        data,
      });
      return resolve(sptRecord);
    } catch (error) {
      return reject(error);
    }
  });
};
export const uploadEIACert = (facilityId, dataURL) => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = `/Facility/${facilityId}/UploadEiaCert`;
      let uploads = null;
      uploads = await makeApiCall({
        url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'post',
        data: { eiaCert: dataURL },
      });
      return resolve(uploads);
    } catch (error) {
      return reject(error);
    }
  });
};

export const lockReport = (reportId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = `/Report/${reportId}/LockReport`;
      let lockReport = null;
      lockReport = await makeApiCall({
        url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'patch',
        data: {},
      });
      return resolve(lockReport);
    } catch (error) {
      return reject(error);
    }
  });
};

export const publishFacilityReport = (reportId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = `/Report/${reportId}/PublishReport`;
      let sptRecord = null;
      sptRecord = await makeApiCall({
        url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'patch',
        data: {},
      });
      return resolve(sptRecord);
    } catch (error) {
      return reject(error);
    }
  });
};

export const rejectFacilityReport = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = `/Report/RejectReport`;
      let sptRecord = null;
      sptRecord = await makeApiCall({
        url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'patch',
        data: data,
      });
      return resolve(sptRecord);
    } catch (error) {
      return reject(error);
    }
  });
};

export const approveFacilityReport = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = `/Report/v2/PublishReport`;
      let sptRecord = null;
      sptRecord = await makeApiCall({
        url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'patch',
        data: data,
      });
      return resolve(sptRecord);
    } catch (error) {
      return reject(error);
    }
  });
};

export const submitFacilityReport = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = `/Report/v2/SubmitReport`;
      let sptRecord = null;
      sptRecord = await makeApiCall({
        url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'patch',
        data: data,
      });
      return resolve(sptRecord);
    } catch (error) {
      return reject(error);
    }
  });
};

export const generateAndSendInvoice = (reportId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = `/Report/${reportId}/GenerateAndSendInvoice`;
      let sptRecord = null;
      sptRecord = await makeApiCall({
        url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'patch',
        data: {},
      });
      return resolve(sptRecord);
    } catch (error) {
      return reject(error);
    }
  });
};

export const generateInvoice = (reportId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = `/Report/${reportId}/GenerateInvoice`;
      let sptRecord = null;
      sptRecord = await makeApiCall({
        url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'patch',
        data: {},
      });
      return resolve(sptRecord);
    } catch (error) {
      return reject(error);
    }
  });
};

export const generateFacilityReport = (facilityId, year) => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = `/Facility/${facilityId}/FacilityReport?year=${year}`;
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      return reject('Error generating facility report');
    }
  });
};

export const generateSectorWideReport = (stateId, year, quarter, sectorId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = `/Report/SamplePoint/Facilities/${stateId}?ReportYear=${year}&ReportQuarter=${quarter}&SectorId=${sectorId}`;
      console.log('url', url);
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      return reject('Error generating facility report');
    }
  });
};

export const generateFacilityInvoice = (reportId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = `/Report/${reportId}/GenerateInvoice`;
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'patch',
      });
      return resolve(fetch);
    } catch (error) {
      return reject('Error generating facility invoice');
    }
  });
};

export const sendFacilityInvoice = (invoiceId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = `/Report/${invoiceId}/SendQAQCInvoice`;
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'patch',
      });
      return resolve(fetch);
    } catch (error) {
      return reject('Error generating facility invoice');
    }
  });
};

export const regenerateFacilityInvoice = (invoiceId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = `/Report/${invoiceId}/ReGenerateInvoice`;
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'patch',
      });
      return resolve(fetch);
    } catch (error) {
      return reject('Error generating facility invoice');
    }
  });
};

export const getProducts = (urlParams) => {
  return new Promise(async (resolve, reject) => {
    try {
      const products = await makeApiCall({
        url: urlParams ? urlParams : `/Product`,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(products);
    } catch (error) {
      return reject('Error fetching list of products');
    }
  });
};

export const getRawMaterials = (urlParams) => {
  return new Promise(async (resolve, reject) => {
    try {
      const rawMaterials = await makeApiCall({
        url: urlParams ? urlParams : `/RawMaterial`,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(rawMaterials);
    } catch (error) {
      return reject('Error fetching list of raw materials');
    }
  });
};

export const getWastes = (urlParams) => {
  return new Promise(async (resolve, reject) => {
    try {
      const wastes = await makeApiCall({
        url: urlParams ? urlParams : `/Waste`,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(wastes);
    } catch (error) {
      return reject('Error fetching list of wastes');
    }
  });
};

export const getSectors = (urlParams) => {
  return new Promise(async (resolve, reject) => {
    try {
      const sectors = await makeApiCall({
        url: urlParams ? urlParams : `/Sector`,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(sectors);
    } catch (error) {
      return reject(error);
    }
  });
};

export const getZoneV2 = () => {
  let url = `/LookUp/Zone`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getStateV2 = () => {
  let url = `/LookUp/State`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getStatePersonalizedV2 = () => {
  let url = `/State/GetStates/V2`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getLgaV2 = () => {
  let url = `/LookUp/Lga`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getSectorV2 = () => {
  let url = `/LookUp/Sector`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getWasteV2 = () => {
  let url = `/LookUp/Waste`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getRawMaterialV2 = () => {
  let url = `/LookUp/RawMaterial`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getProductV2 = () => {
  let url = `/LookUp/Product`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getRegions = (urlParams) => {
  return new Promise(async (resolve, reject) => {
    try {
      const zones = await makeApiCall({
        url: urlParams ? urlParams : `/Zone`,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(zones);
    } catch (error) {
      return reject('Error fetching list of regions');
    }
  });
};

export const getParameterGroups = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const { data: parameterGroups } = await makeApiCall({
        url: '/Lookup/ParameterGroup',
        method: 'get',
      });
      console.log(parameterGroups, 'from api js');
      return resolve(parameterGroups);
    } catch (error) {
      return reject('Error fetching parameter groups');
    }
  });
};

export const getPayableAmount = ({ facilityId, quarter }) => {
  return new Promise(async (resolve, reject) => {
    try {
      const {
        payment: { amount, paymentType, paymentYear, paymentPeriod },
      } = await makeApiCall({
        url: `/Payment/GetPayableAmount/${facilityId}/${quarter}`,
        method: 'get',
      });
      return resolve({ amount, paymentType, paymentYear, paymentPeriod });
    } catch (error) {
      return reject('Could not determine amount to be paid');
    }
  });
};

export const checkReportStatus = ({ facilityId, quarter, accountId }) => {
  return new Promise(async (resolve, reject) => {
    try {
      const { currentStatus, samplepoint } = await makeApiCall({
        url: `ReportSchedule/ReportStatus/${facilityId}/${new Date().getFullYear()}/${quarter}/${accountId}`,
        method: 'get',
      });
      return resolve({ currentStatus, samplepoint });
    } catch (error) {
      return reject('Could not check report status');
    }
  });
};

export const initiatePayment = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const { refNo } = await makeApiCall({
        url: '/Payment/MakePayment',
        method: 'post',
        data,
      });
      return resolve(refNo);
    } catch (error) {
      return reject('Could not generate payment reference');
    }
  });
};

export const createOrLoadReport = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const {
        report: { reportId, reportTitle },
      } = await makeApiCall({
        url: '/ReportSchedule/Create',
        method: 'post',
        data,
      });
      return resolve({ reportId, reportTitle });
    } catch (data) {
      if (data && data.report) {
        const {
          report: { reportTitle, reportId },
        } = data;
        return resolve({ reportId, reportTitle });
      } else {
        return reject('Something went wrong, please contact the system admin');
      }
    }
  });
};

export const getSamplePoints = ({ facilityId, group, quarter }) => {
  return new Promise(async (resolve, reject) => {
    try {
      const { data } = await makeApiCall({
        url: `/Lookup/SamplePoint/${facilityId}/${group}/${quarter}`,
        method: 'get',
      });
      return resolve(data);
    } catch (error) {
      return reject('Something went wrong, please contact the system admin');
    }
  });
};

export const getResultData = ({ facilityId, reportId, group }) => {
  return new Promise(async (resolve, reject) => {
    try {
      const { resultsData } = await makeApiCall({
        url: `/Results/ResultData/${reportId}/${facilityId}/${group}`,
        method: 'get',
      });
      return resolve(resultsData);
    } catch (error) {
      return reject('Something went wrong, please contact the system admin');
    }
  });
};

export const getUnits = ({ group }) => {
  return new Promise(async (resolve, reject) => {
    try {
      const { data } = await makeApiCall({
        url: `/Lookup/ParameterUnit/${group}`,
        method: 'get',
      });
      return resolve(data);
    } catch (error) {
      return reject('Something went wrong, please contact the system admin');
    }
  });
};

export const saveResultData = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      await makeApiCall({
        url: '/Results/Create',
        method: 'post',
        data,
      });
      return resolve('Sample point data saved successfully');
    } catch (error) {
      return reject('Could not save result data, please contact the system admin');
    }
  });
};

export const login = ({ username, password }) => {
  return new Promise(async (resolve, reject) => {
    try {
      const account = await makeApiCall({
        url: '/Account/Login',
        method: 'post',
        data: {
          username,
          password,
        },
      });
      return resolve(account);
    } catch (error) {
      return reject(error);
    }
  });
};

export const getStates = (urlParam) => {
  const token = getToken();
  return new Promise(async (resolve, reject) => {
    try {
      const states = await makeApiCall({
        url: urlParam ? urlParam : '/State',
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });

      return resolve(states);
    } catch (error) {
      return reject(error);
    }
  });
};

export const getLgas = (urlParam) => {
  return new Promise(async (resolve, reject) => {
    try {
      const lgas = await makeApiCall({
        url: urlParam ? urlParam : '/Lga',
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });

      return resolve(lgas);
    } catch (error) {
      return reject('Error fetching lists of local government areas');
    }
  });
};

export const getConsultants = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const consultants = await makeApiCall({
        url: '/Account?UserCat=3',
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(consultants);
    } catch (error) {
      return reject('Error fetching lists of consultants');
    }
  });
};

export const getMeasurements = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const measurements = await makeApiCall({
        url: '/Measurement/GetMeasurement/V2',
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });

      return resolve(measurements);
    } catch (error) {
      return reject('Error fetching lists of measurements');
    }
  });
};

export const getStandardSamplePoints = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const measurements = await makeApiCall({
        url: '/SamplePoint/GetStandardSamplePoints/V2',
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });

      return resolve(measurements);
    } catch (error) {
      return reject('Error fetching lists of measurements');
    }
  });
};

export const getStandardParameters = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const measurements = await makeApiCall({
        url: '/Measurement/getStandardParameter/V2',
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });

      return resolve(measurements);
    } catch (error) {
      return reject('Error fetching lists of measurements');
    }
  });
};

export const getUserRoles = (accountId) => {
  const token = getToken();
  return new Promise(async (resolve, reject) => {
    try {
      const roles = await makeApiCall({
        url: `/role?AccountId=${accountId}`,
        method: 'get',
        headers: { Authorization: `bearer ${token}` },
      });
      return resolve(roles);
    } catch (error) {
      return reject(error);
    }
  });
};

// SEND EMAIL INVITE TO USERS
export const inviteUsers = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const users = await makeApiCall({
        url: '/UserInvite',
        method: 'post',
        headers: { Authorization: `bearer ${getToken()}` },
        data: data,
      });

      return resolve(users);
    } catch (error) {
      return reject(error);
    }
  });
};

export const getUserEmail = (id) => {
  return new Promise(async (resolve, reject) => {
    try {
      const userEmail = await makeApiCall({
        url: `/UserInvite/${id}/GetEmail`,
        method: 'get',
      });
      return resolve(userEmail);
    } catch (error) {
      if (
        error &&
        error.result &&
        error.result.status &&
        error.result.status.desc === 'NOT FOUND'
      ) {
        return resolve(error);
      }
      return reject(error);
    }
  });
};
export const createAdhocReport = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const adhocReport = await makeApiCall({
        url: '/Report',
        method: 'post',
        headers: { Authorization: `bearer ${getToken()}` },
        data,
      });

      return resolve(adhocReport);
    } catch (error) {
      if (!error) reject('Report Title Already Exists!');
      return reject(error);
    }
  });
};
export const createFacility = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const facility = await makeApiCall({
        url: '/Facility',
        method: 'post',
        headers: { Authorization: `bearer ${getToken()}` },
        data: data,
      });

      return resolve(facility);
    } catch (error) {
      return reject(error);
    }
  });
};

export const updateFacility = (facilityId, data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const facility = await makeApiCall({
        url: '/Facility/' + facilityId,
        method: 'put',
        headers: { Authorization: `bearer ${getToken()}` },
        data: data,
      });

      return resolve(facility);
    } catch (error) {
      return reject(error);
    }
  });
};

export const updateFacilityStandardSamplePoint = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const facility = await makeApiCall({
        url: '/Facility/UpdateFacilityStandardSamplePoint',
        method: 'put',
        headers: { Authorization: `bearer ${getToken()}` },
        data: data,
      });

      return resolve(facility);
    } catch (error) {
      return reject(error);
    }
  });
};

export const forgetPassword = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const reset = await makeApiCall({
        url: '/Account/PasswordForget',
        method: 'post',
        data: data,
      });

      return resolve(reset);
    } catch (error) {
      return reject(error);
    }
  });
};

export const resetPassword = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const reset = await makeApiCall({
        url: '/Account/PasswordReset',
        method: 'post',
        data: data,
      });

      return resolve(reset);
    } catch (error) {
      return reject(error);
    }
  });
};

export const changePassword = (data) => {
  const token = getToken();
  return new Promise(async (resolve, reject) => {
    try {
      const reset = await makeApiCall({
        url: '/Account/PasswordChange',
        method: 'post',
        data: data,
        headers: { Authorization: `bearer ${token}` },
      });
      return resolve(reset);
    } catch (error) {
      return reject('Reset failed. Unable to change password');
    }
  });
};

export const getPrivileges = () => {
  const token = getToken();
  return new Promise(async (resolve, reject) => {
    try {
      const privieleges = await makeApiCall({
        url: '/Privilege',
        method: 'get',
        headers: { Authorization: `bearer ${token}` },
      });
      return resolve(privieleges);
    } catch (error) {
      return reject('Something went wrong, Could not get privileged');
    }
  });
};

export const createRole = (data) => {
  const token = getToken();
  return new Promise(async (resolve, reject) => {
    try {
      const role = await makeApiCall({
        url: '/Role',
        method: 'post',
        data: data,
        headers: { Authorization: `bearer ${token}` },
      });
      return resolve(role);
    } catch (error) {
      return reject('Something went wrong. Could not create new role');
    }
  });
};
// GET LIST OF GEOPOLITICAL ZONES
export const getZones = () => {
  const token = getToken();
  return new Promise(async (resolve, reject) => {
    try {
      const zones = await makeApiCall({
        url: '/Zone',
        method: 'get',
        headers: { Authorization: `bearer ${token}` },
      });
      return resolve(zones);
    } catch (error) {
      return reject(error);
    }
  });
};

// GET LIST OF LGA
export const getLga = (params) => {
  const token = getToken();
  return new Promise(async (resolve, reject) => {
    let url = `/Lga?Start=0&Stop=100000`;
    if (params) url = `/Lga${params}`;
    try {
      const lga = await makeApiCall({
        url: url,
        method: 'get',
        headers: { Authorization: `bearer ${token}` },
      });

      return resolve(lga);
    } catch (error) {
      console.log('fetching error', error);
      if (error && error.result && error.result.status) return resolve(error);
      if (!error) return resolve(null);
      return reject('Error getting list of local governmant areas');
    }
  });
};

// GET LIST OF USERS
export const getUsers = (params) => {
  return new Promise(async (resolve, reject) => {
    let url = `/Account?Start=0&Stop=100000`;
    if (params) url = `/Account${params}`;
    try {
      const users = await makeApiCall({
        url: url,
        method: 'get',
        headers: { Authorization: `bearer ${getToken()}` },
      });

      return resolve(users);
    } catch (error) {
      return reject('Something went wrong. Could not get users.');
    }
  });
};

export const signupConsultant = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const signup = await makeApiCall({
        url: `/Consultant`,
        method: 'post',
        data: data,
      });

      return resolve(signup);
    } catch (error) {
      return reject(error);
    }
  });
};

// ACCEPT INVITE USER
export const signup = (data, id) => {
  return new Promise(async (resolve, reject) => {
    try {
      const signup = await makeApiCall({
        url: `/Account?InviteId=${id}`,
        method: 'post',
        data: data,
      });

      return resolve(signup);
    } catch (error) {
      return reject('Something went wrong. Could not sign up.');
    }
  });
};

// UPDATE PROFILE DETAILS
export const updateProfile = (data, userId) => {
  const token = getToken();
  return new Promise(async (resolve, reject) => {
    try {
      const profile = await makeApiCall({
        url: `/Account/${userId}`,
        method: 'put',
        headers: { Authorization: `bearer ${token}` },
        data: data,
      });

      return resolve(profile);
    } catch (error) {
      return reject(error);
    }
  });
};

// GET LOOKUP DATA
export const getLookups = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const lookups = await makeApiCall({
        url: '/LookUp',
        method: 'get',
        header: { Authorization: `bearer ${getToken()}` },
      });
      return resolve(lookups);
    } catch (error) {
      return reject('Something went wrong could not get lookups');
    }
  });
};

// GET FACILITIES IN A SECTOR
export const sectorFacility = (id) => {
  return new Promise(async (resolve, reject) => {
    try {
      const facilities = await makeApiCall({
        url: `/Sector/${id}/Facilities`,
        method: 'get',
      });
      return resolve(facilities);
    } catch (error) {
      return reject('Something went wrong could not get facilities in this sector');
    }
  });
};

// GET FACILITIES IN LGA
export const lgaFacility = (id) => {
  return new Promise(async (resolve, reject) => {
    try {
      const facilities = await makeApiCall({
        url: `/Lga/${id}/Facilities`,
        method: 'get',
      });
      return resolve(facilities);
    } catch (error) {
      return reject('Something went wrong could not get facilities in this Local Govt');
    }
  });
};

// CREATE NEW STATE
export const createState = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const state = await makeApiCall({
        url: '/State',
        method: 'post',
        headers: { Authorization: `bearer ${getToken()}` },
        data,
      });
      return resolve(state);
    } catch (error) {
      if (error && error.result && error.result.status && error.result.status.desc === 'EMPTY')
        return resolve(error);
      if (error && error.result && error.result.status && error.result.status.desc === 'NOT FOUND')
        return resolve(error);
      if (
        error &&
        error.result &&
        error.result.status &&
        error.result.status.desc === 'ALREADY EXISTS'
      )
        return reject('State already exists');
      if (!error) return reject('State already exists');
      return reject(error);
    }
  });
};

// CREATE NEW LOCAL GOVT
export const createLga = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const Lga = await makeApiCall({
        url: '/Lga',
        method: 'post',
        headers: { Authorization: `bearer ${getToken()}` },
        data: data,
      });
      return resolve(Lga);
    } catch (error) {
      return reject(error);
    }
  });
};

// CREATE NEW SECTOR
export const createSector = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const sector = await makeApiCall({
        url: '/Sector',
        method: 'post',
        headers: { Authorization: `bearer ${getToken()}` },
        data: data,
      });
      return resolve(sector);
    } catch (error) {
      return reject(error);
    }
  });
};

// UPDATE ZONE
export const updateZone = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const zone = await makeApiCall({
        url: '/Zone',
        method: 'put',
        headers: { Authorization: `bearer ${getToken()}` },
        data: data,
      });
      return resolve(zone);
    } catch (error) {
      return reject('Something went wrong could update zone');
    }
  });
};

// UPDATE STATES
export const updateState = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const zone = await makeApiCall({
        url: '/State',
        method: 'put',
        headers: { Authorization: `bearer ${getToken()}` },
        data: data,
      });
      return resolve(zone);
    } catch (error) {
      if (!error) reject('Cannot update to an existing state name');
      return reject(error);
    }
  });
};

// UPDATE LGA
export const updateLGA = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const LGA = await makeApiCall({
        url: '/Lga',
        method: 'put',
        headers: { Authorization: `bearer ${getToken()}` },
        data: data,
      });
      return resolve(LGA);
    } catch (error) {
      console.log('fetching error', error);
      if (error && error.result && error.result.status && error.result.status.desc === 'EMPTY')
        return resolve(error);
      if (error && error.result && error.result.status && error.result.status.desc === 'NOT FOUND')
        return resolve(error);
      if (
        error &&
        error.result &&
        error.result.status &&
        error.result.status.desc === 'ALREADY EXISTS'
      )
        return resolve(error);
      if (!error) return resolve(null);
      return reject(error);
    }
  });
};

// UPDATE SECTOR
export const updateSector = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const sector = await makeApiCall({
        url: '/Sector',
        method: 'put',
        headers: { Authorization: `bearer ${getToken()}` },
        data: data,
      });
      return resolve(sector);
    } catch (error) {
      return reject(error);
    }
  });
};

// GET DASHBOARD DATA
export const getDashboardData = (params) => {
  let url = '/Dashboard';
  if (params) url = `/Dashboard${params}`;
  return new Promise(async (resolve, reject) => {
    try {
      const dashboard = await makeApiCall({
        url,
        method: 'get',
        headers: { Authorization: `bearer ${getToken()}` },
      });
      return resolve(dashboard);
    } catch (error) {
      return reject(error);
    }
  });
};

// GET DASHBOARD STAT
export const getDashboardStat = (params) => {
  let url = '/Dashboard';
  if (params) {
    if (params === "?StateId=''") url = `/Dashboard/Stats`;
    else url = `/Dashboard/Stats${params}`;
  }
  // if (param) url = `/Dashboard/Stats?StateId=101`;
  return new Promise(async (resolve, reject) => {
    try {
      const dashboardStat = await makeApiCall({
        url,
        method: 'get',
        headers: { Authorization: `bearer ${getToken()}` },
      });
      return resolve(dashboardStat);
    } catch (error) {
      return reject(error);
    }
  });
};

export const createZone = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const zone = await makeApiCall({
        url: '/Zone',
        method: 'post',
        headers: { Authorization: `bearer ${getToken()}` },
        data: data,
      });
      return resolve(zone);
    } catch (error) {
      return reject('Something went wrong could not create new zone');
    }
  });
};

export const updateRole = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const role = await makeApiCall({
        url: '/Role',
        method: 'put',
        headers: { Authorization: `bearer ${getToken()}` },
        data: data,
      });
      return resolve(role);
    } catch (error) {
      console.log('fetching error', error);
      if (error && error.result && error.result.status && error.result.status.desc === 'EMPTY')
        return resolve(error);
      if (error && error.result && error.result.status && error.result.status.desc === 'NOT FOUND')
        return resolve(error);
      if (
        error &&
        error.result &&
        error.result.status &&
        error.result.status.desc === 'ALREADY EXISTS'
      )
        return resolve(error);
      if (!error) return resolve(null);
      return reject('Error updating this role');
    }
  });
};

export const getPendingUsers = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const pendingUsers = await makeApiCall({
        url: '/UserInvite',
        method: 'get',
        headers: { Authorization: `bearer ${getToken()}` },
      });
      return resolve(pendingUsers);
    } catch (error) {
      console.log('fetching error', error);
      if (error && error.result && error.result.status && error.result.status.desc === 'EMPTY')
        return resolve(error);
      if (error && error.result && error.result.status && error.result.status.desc === 'NOT FOUND')
        return resolve(error);
      if (!error) return resolve(null);
      return reject('Error fetching list of users');
    }
  });
};

export const deleteState = (stateId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const state = await makeApiCall({
        url: `/State/${stateId}`,
        method: 'delete',
        headers: { Authorization: `bearer ${getToken()}` },
      });
      return resolve(state);
    } catch (error) {
      console.log('fetching error', error);
      if (error && error.result && error.result.status && error.result.status.desc === 'EMPTY')
        return resolve(error);
      if (error && error.result && error.result.status && error.result.status.desc === 'NOT FOUND')
        return resolve(error);
      if (!error) return resolve(null);
      return reject('Error deleting state');
    }
  });
};

export const updateUser = (UserId, data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const user = await makeApiCall({
        url: `/Account/${UserId}/AddAreaPermissions`,
        method: 'put',
        headers: { Authorization: `bearer ${getToken()}` },
        data: data,
      });
      return resolve(user);
    } catch (error) {
      console.log('fetching error', error);
      if (error && error.result && error.result.status && error.result.status.desc === 'EMPTY')
        return resolve(error);
      if (error && error.result && error.result.status && error.result.status.desc === 'NOT FOUND')
        return resolve(error);
      if (!error) return resolve(null);
      return reject(error);
    }
  });
};

export const deleteUserAssignedAreas = (UserId, data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const user = await makeApiCall({
        url: `/Account/${UserId}/RemoveAreaPermissions`,
        method: 'delete',
        headers: { Authorization: `bearer ${getToken()}` },
        data: data,
      });
      return resolve(user);
    } catch (error) {
      console.log('fetching error', error);
      if (error && error.result && error.result.status && error.result.status.desc === 'EMPTY')
        return resolve(error);
      if (error && error.result && error.result.status && error.result.status.desc === 'NOT FOUND')
        return resolve(error);
      if (!error) return resolve(null);
      return reject(error);
    }
  });
};

export const addUserAssignedAreas = (UserId, data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const user = await makeApiCall({
        url: `/Account/${UserId}/AddAreaPermissions`,
        method: 'post',
        headers: { Authorization: `bearer ${getToken()}` },
        data: data,
      });
      return resolve(user);
    } catch (error) {
      console.log('fetching error', error);
      if (error && error.result && error.result.status && error.result.status.desc === 'EMPTY')
        return resolve(error);
      if (error && error.result && error.result.status && error.result.status.desc === 'NOT FOUND')
        return resolve(error);
      if (!error) return resolve(null);
      return reject(error);
    }
  });
};

export const deleteLga = (lgaId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const state = await makeApiCall({
        url: `/Lga/${lgaId}`,
        method: 'delete',
        headers: { Authorization: `bearer ${getToken()}` },
      });
      return resolve(state);
    } catch (error) {
      console.log('fetching error', error);
      if (error && error.result && error.result.status && error.result.status.desc === 'EMPTY')
        return resolve(error);
      if (error && error.result && error.result.status && error.result.status.desc === 'NOT FOUND')
        return resolve(error);
      if (!error) return resolve(null);
      return reject(error);
    }
  });
};

export const searchLga = (stateId) => {
  const token = getToken();
  return new Promise(async (resolve, reject) => {
    try {
      const lga = await makeApiCall({
        url: `/State/${stateId}/GetLga`,
        method: 'get',
        headers: { Authorization: `bearer ${token}` },
      });

      return resolve(lga);
    } catch (error) {
      console.log('fetching error', error);
      if (error && error.result && error.result.status) return resolve(error);
      if (!error) return resolve(null);
      return reject(error);
    }
  });
};

export const getUser = (userId) => {
  const token = getToken();
  return new Promise(async (resolve, reject) => {
    try {
      const user = await makeApiCall({
        url: `/Account?id=${userId}`,
        method: 'get',
        headers: { Authorization: `bearer ${token}` },
      });

      return resolve(user);
    } catch (error) {
      if (error && error.result && error.result.status) return resolve(error);
      if (!error) return resolve(null);
      return reject(error);
    }
  });
};
export const deleteZone = (zoneId) => {
  const token = getToken();
  return new Promise(async (resolve, reject) => {
    try {
      const zone = await makeApiCall({
        url: `/Zone/${zoneId}`,
        method: 'delete',
        headers: { Authorization: `bearer ${token}` },
      });

      return resolve(zone);
    } catch (error) {
      console.log('fetching error', error);
      if (error && error.result && error.result.status) return resolve(error);
      if (!error) return resolve(null);
      return reject(error);
    }
  });
};

// GET FACILITY BY SCHEDULE
export const getScheduleFacility = (quarter = 0, stateId = '', limit = 100) => {
  const currentYear = new Date().getFullYear();
  return new Promise(async (resolve, reject) => {
    //let url = `/Schedule?quarter=${quarter}&year=${currentYear}&limit=${limit}`;
    let url = `/Schedule?quarter=${quarter}&limit=${limit}`;
    let stateURL = `/Schedule?quarter=${quarter}&stateId=${stateId}&year=${currentYear}&limit=${limit}`;
    try {
      const scheduledFacility = await makeApiCall({
        url: stateId !== '' ? stateURL : url,
        method: 'get',
        headers: { Authorization: `bearer ${getToken()}` },
      });

      return resolve(scheduledFacility);
    } catch (error) {
      if (error && error.result && error.result.status) return resolve(error);
      return reject(error);
    }
  });
};

export const getScheduleFacility2 = (search, year, quarter, stateId, sectorId, offset, limit) => {
  return new Promise(async (resolve, reject) => {
    let url = `/Schedule?search=${search}&year=${year}&quarter=${quarter}&stateId=${stateId}&sectorId=${sectorId}&offset=${offset}&limit=${limit}`;
    try {
      const scheduledFacility = await makeApiCall({
        url: url,
        method: 'get',
        headers: { Authorization: `bearer ${getToken()}` },
      });

      return resolve(scheduledFacility);
    } catch (error) {
      if (error && error.result && error.result.status) return resolve(error);
      return reject(error);
    }
  });
};

// GET SCHEDULE METRICS
export const getScheduleMetrics = (year, stateId) => {
  return new Promise(async (resolve, reject) => {
    let url = `/Schedule/Metrics?year=${year}&stateId=${stateId}`;
    try {
      const scheduledMetrics = await makeApiCall({
        url: url,
        method: 'get',
        headers: { Authorization: `bearer ${getToken()}` },
      });

      return resolve(scheduledMetrics);
    } catch (error) {
      return reject(error);
    }
  });
};

// SCHEDULE FACILITY
export const scheduleFacility = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const scheduledMetrics = await makeApiCall({
        url: '/Schedule',
        method: 'post',
        headers: { Authorization: `bearer ${getToken()}` },
        data: data,
      });

      return resolve(scheduledMetrics);
    } catch (error) {
      //console.log('schedule error', error);
      if (error && error.result && error.result.status) return resolve(error);
      return reject(error);
    }
  });
};

// GET ADMIN REPORT MENU
export const adminReportMenu = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const reportMenu = await makeApiCall({
        url: 'TelerikReports/ReportMenu',
        method: 'get',
        headers: { Authorization: `bearer ${getToken()}` },
      });

      return resolve(reportMenu);
    } catch (error) {
      return reject(error);
    }
  });
};

// CREATE NEW MEASUREMENT
export const createMeasurements = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const measurements = await makeApiCall({
        url: '/Measurement/CreateMeasurement/V2',
        method: 'post',
        headers: { Authorization: `bearer ${getToken()}` },
        data: data,
      });
      return resolve(measurements);
    } catch (error) {
      reject(error);
    }
  });
};

// GET NOTIFICATION SETTINGS
export const getNotifications = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const notifications = await makeApiCall({
        url: '/NotificationSetting',
        method: 'get',
        headers: { Authorization: `bearer ${getToken()}` },
      });
      return resolve(notifications);
    } catch (error) {
      reject(error);
    }
  });
};

// ADD PARAMETER TO MEASUREMENT
export const addParameters = (data, measurementId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const parameters = await makeApiCall({
        url: `/Measurement/AddMeasurementParameters/V2/${measurementId}`,
        method: 'post',
        headers: { Authorization: `bearer ${getToken()}` },
        data: data,
      });
      return resolve(parameters);
    } catch (error) {
      reject(error);
    }
  });
};

// UPDATE MEASUREMENT TYPE
export const updateMeasurement = (data, id) => {
  return new Promise(async (resolve, reject) => {
    try {
      const measurement = await makeApiCall({
        url: `/Measurement/${id}`,
        method: 'put',
        headers: { Authorization: `bearer ${getToken()}` },
        data: data,
      });
      return resolve(measurement);
    } catch (error) {
      reject(error);
    }
  });
};

// UPDATE NOTIFICATION SETTINGS
export const disableNotification = (settingName) => {
  return new Promise(async (resolve, reject) => {
    try {
      const notification = await makeApiCall({
        url: `/NotificationSetting/${settingName}/Disable`,
        method: 'put',
        headers: { Authorization: `bearer ${getToken()}` },
      });
      return resolve(notification);
    } catch (error) {
      reject(error);
    }
  });
};

export const enableNotification = (settingName) => {
  return new Promise(async (resolve, reject) => {
    try {
      const notification = await makeApiCall({
        url: `/NotificationSetting/${settingName}/Enable`,
        method: 'put',
        headers: { Authorization: `bearer ${getToken()}` },
      });
      return resolve(notification);
    } catch (error) {
      reject(error);
    }
  });
};

// REMOVER PARAMETERS FROM MEASUREMENT
export const removeMeasurementParameters = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const measurementParameters = await makeApiCall({
        url: `/Measurement/RemoveMeasurementParameter/V2`,
        method: 'post',
        data: data,
        headers: { Authorization: `bearer ${getToken()}` },
      });
      return resolve(measurementParameters);
    } catch (error) {
      reject(error);
    }
  });
};

// GET USER PREFERENCES
export const fetchPreferenceSetting = (userId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const notifications = await makeApiCall({
        url: `/PreferenceSetting/${userId}`,
        method: 'get',
        headers: { Authorization: `bearer ${getToken()}` },
      });
      return resolve(notifications);
    } catch (error) {
      reject(error);
    }
  });
};

// ENABLE USER PREFERENCE
export const enableUserPreference = (userId, preferenceName) => {
  return new Promise(async (resolve, reject) => {
    try {
      const preference = await makeApiCall({
        url: `/PreferenceSetting/${userId}/Enable/${preferenceName}`,
        method: 'post',
        headers: { Authorization: `bearer ${getToken()}` },
      });
      return resolve(preference);
    } catch (error) {
      reject(error);
    }
  });
};

// DISABLE USER PREFERENCE
export const disableUserPreference = (userId, preferenceName) => {
  return new Promise(async (resolve, reject) => {
    try {
      const preference = await makeApiCall({
        url: `/PreferenceSetting/${userId}/Disable/${preferenceName}`,
        method: 'post',
        headers: { Authorization: `bearer ${getToken()}` },
      });
      return resolve(preference);
    } catch (error) {
      reject(error);
    }
  });
};

// GET ROLES ACCORDING TO USER CATEGORY
export const getRolesByUserCategory = (userCategory) => {
  return new Promise(async (resolve, reject) => {
    try {
      const preference = await makeApiCall({
        url: `/Role/${userCategory}`,
        method: 'get',
        headers: { Authorization: `bearer ${getToken()}` },
      });
      return resolve(preference);
    } catch (error) {
      reject(error);
    }
  });
};

// DELETE SECTOR
export const deleteSector = (sectorId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const sector = await makeApiCall({
        url: `/Sector/${sectorId}`,
        method: 'delete',
        headers: { Authorization: `bearer ${getToken()}` },
      });
      return resolve(sector);
    } catch (error) {
      reject(error);
    }
  });
};

// EDIT MEASUREMENT PARAMETER
export const updateParameter = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const parameter = await makeApiCall({
        url: `/Measurement/UpdateMeasurementParameter/V2`,
        method: 'put',
        headers: { Authorization: `bearer ${getToken()}` },
        data: data,
      });
      return resolve(parameter);
    } catch (error) {
      reject(error);
    }
  });
};

export const getFacilityCategory = (urlParams) => {
  return new Promise(async (resolve, reject) => {
    try {
      const sectors = await makeApiCall({
        url: urlParams ? urlParams : `/Facility/FacilityCategory`,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(sectors);
    } catch (error) {
      return reject(error);
    }
  });
};

export const getFacilityCategoryV2 = () => {
  let url = `/Facility/FacilityCategory`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getFacilityCategoryReport = (categoryId, quarter, year, measurementId, stateId) => {
  let url = `/Report/IemsFacilityCategoryReport/${categoryId}/${quarter}/${year}/${measurementId}?StateId=${stateId}`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'patch',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getBasicFacilityReport = (facilityId, year, quarter) => {
  let url = `/Report/SamplePoint/${facilityId}?ReportYear=${year}&ReportQuarter=${quarter}`;
  console.log('url', url);
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getQuarterlyComparisonReport = (facilityId, year, measurementId) => {
  let url = `/Report/QuarterlyComparison/${facilityId}/${year}/${measurementId}`;
  console.log('url', url);
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getAuditLog = (startDate, endDate) => {
  let url = `/AuditLog?FromDate=${startDate}&ToDate=${endDate}`;
  console.log(url);
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getInvoices = (search, facilityId, status, reportId, start, stop) => {
  const url = `/Payment/Invoice?search=${search}&facilityId=${facilityId}&status=${status}&reportId=${reportId}&start=${start}&stop=${stop}`;
  return new Promise(async (resolve, reject) => {
    try {
      const sectors = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(sectors);
    } catch (error) {
      return reject(error);
    }
  });
};

export const getPayments = (
  facilityId,
  referenceNo,
  invoiceId,
  status,
  year,
  paymentMethod,
  paymentGateWay,
  start,
  stop
) => {
  const url = `/Payment/Payments?facilityId=${facilityId}&referenceNo=${referenceNo}&invoiceId=${invoiceId}&status=${status}&year=${year}&paymentMethod=${paymentMethod}&paymentGateWay=${paymentGateWay}&start=${start}&stop=${stop}`;
  //console.log(url);

  return new Promise(async (resolve, reject) => {
    try {
      const sectors = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(sectors);
    } catch (error) {
      return reject(error);
    }
  });
};

export const getPaymentSummary = (year, stateId, facilityId) => {
  const url = `/Payment/PaymentSummary?year=${year}&stateId=${stateId}&facilityId=${facilityId}`;
  //console.log(url);

  return new Promise(async (resolve, reject) => {
    try {
      const sectors = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(sectors);
    } catch (error) {
      return reject(error);
    }
  });
};

export const getInvoiceById = (invoiceId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const account = await makeApiCall({
        url: `/Payment/Invoice/${invoiceId}`,
        method: 'get',
        // data: {
        //   username,
        //   password,
        // },
      });
      return resolve(account);
    } catch (error) {
      return reject(error);
    }
  });
};

export const initializePayment = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const parameter = await makeApiCall({
        url: `/Payment/Initialize`,
        method: 'post',
        headers: { Authorization: `bearer ${getToken()}` },
        data: data,
      });
      return resolve(parameter);
    } catch (error) {
      reject(error);
    }
  });
};

export const verifyPayment = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const parameter = await makeApiCall({
        url: `/Payment/Verify`,
        method: 'post',
        headers: { Authorization: `bearer ${getToken()}` },
        data: data,
      });
      return resolve(parameter);
    } catch (error) {
      reject(error);
    }
  });
};

export const submitPayment = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const parameter = await makeApiCall({
        url: `/Payment/SubmitPayment`,
        method: 'post',
        headers: {
          Authorization: `bearer ${getToken()}`,
          // 'Content-Type': 'multipart/form-data',
        },
        data: data,
      });
      return resolve(parameter);
    } catch (error) {
      reject(error);
    }
  });
};

export const approvePayment = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const parameter = await makeApiCall({
        url: `/Payment/ApprovePayment`,
        method: 'post',
        headers: {
          Authorization: `bearer ${getToken()}`,
        },
        data: data,
      });
      return resolve(parameter);
    } catch (error) {
      reject(error);
    }
  });
};

export const resendInvoice = (reportId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const parameter = await makeApiCall({
        url: `/Report/${reportId}/ResendInvoice`,
        method: 'patch',
        headers: { Authorization: `bearer ${getToken()}` },
        data: {},
      });
      return resolve(parameter);
    } catch (error) {
      reject(error);
    }
  });
};

export const generateCert = (reportId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await makeApiCall({
        url: `/Report/${reportId}/GenerateCertificate`,
        method: 'patch',
        headers: { Authorization: `bearer ${getToken()}` },
        data: {},
      });
      return resolve(response);
    } catch (error) {
      reject(error);
    }
  });
};

export const sendCert = (reportId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await makeApiCall({
        url: `/Report/${reportId}/SendCertificate`,
        method: 'patch',
        headers: { Authorization: `bearer ${getToken()}` },
        data: {},
      });
      return resolve(response);
    } catch (error) {
      reject(error);
    }
  });
};

export const toggleQaqcReportVisibilityApi = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const parameter = await makeApiCall({
        url: `/Report/ToggleQAQCReportVisibility`,
        method: 'patch',
        headers: { Authorization: `bearer ${getToken()}` },
        data: data,
      });
      return resolve(parameter);
    } catch (error) {
      reject(error);
    }
  });
};

// export const getEdmDashboardStats = () => {
//   const url = `Dashboard/Edm/StatCards/V2`;
//   console.log(url);
//   return new Promise(async (resolve, reject) => {
//     try {
//       const sectors = await makeApiCall({
//         url: url,
//         //headers: { Authorization: `bearer ${getToken()}` },
//         method: 'get',
//       });
//       return resolve(sectors);
//     } catch (error) {
//       return reject(error);
//     }
//   });
// };

export const getEdmDashboardStats = (stateId = '') => {
  let url = `/Dashboard/Edm/StatCards/V2?stateId=${stateId}`;

  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getEdmPendingPayments = (stateId = '') => {
  let url = `/Dashboard/Edm/PendingPayments/V2?stateId=${stateId}`;

  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        //headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getEdmDashboardComparativeReportFacilityList = (stateId = '') => {
  let url = `/Dashboard/Edm/CamparativeReportFacilityList/V2?stateId=${stateId}`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        //headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getEdmDashboardComparativeMeasurementFacilityList = () => {
  let url = `/Dashboard/Edm/ComparativeReportMeasurementList/V2`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        //headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getEdmDashboardComparativeReport = (facilityId, measurementId) => {
  let url = `/Dashboard/Edm/ComparativeReport/V2/${facilityId}/${measurementId}`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        //headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getEdmDashboardViolations = (limitType) => {
  let url = `/Dashboard/Edm/V2/${limitType}/FacilityViolationList`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        //headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getFacilitiesForMeasurementAnalytics = (measurementId, sectorId, stateId) => {
  let url = `/Analytics/MeasurementFacilities/${measurementId}?SectorId=${sectorId}&StateId=${stateId}`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        //headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getFacilityComparativeAnalytics = (
  measurementId,
  facilityId,
  year,
  q1,
  q2,
  q3,
  q4
) => {
  let url = `/Analytics/MeasurementResults/${measurementId}/${facilityId}/${year}?Q1=${q1}&Q2=${q2}&Q3=${q3}&Q4=${q4}`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        //headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getStateLookup = () => {
  let url = `/Lookup/State`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getLgaLookup = () => {
  let url = `/Lookup/Lga`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getDashboardSectorAnalyticsByState = (zoneId, stateId, LGAId) => {
  let url = `/Dashboard/FacilitySectorData/V2?ZoneId=${zoneId}&StateId=${stateId}&LGAId=${LGAId}`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getDashboardSectorAnalyticsByLga = (zoneId, stateId, LGAId) => {
  let url = `/Dashboard/FacilitySectorData/V2?ZoneId=${zoneId}&StateId=${stateId}&LGAId=${LGAId}`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getDashboardFacilityCategory = (zoneId, stateId, LGAId) => {
  let url = `/Dashboard/FacilityCategoryData/V2?ZoneId=${zoneId}&StateId=${stateId}&LGAId=${LGAId}`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getEquipments = () => {
  let url = `/Equipment`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getFacilityAnalyticsSamplePoints = (facilityId) => {
  let url = `/TelerikReports/GetSamplingPointsForFacility/${facilityId}`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: {
          //Authorization: `bearer ${getToken()}`
        },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

// export const getFacilityAnalyticsSamplePoints = (facilityId) => {
//   let url = `/TelerikReports/GetSamplingPointsForFacility/${facilityId}`;
//   return new Promise(async (resolve, reject) => {
//     try {
//       const fetch = await makeApiCall({
//         url: url,
//         headers: {
//           //Authorization: `bearer ${getToken()}`
//         },
//         method: 'get',
//       });
//       return resolve(fetch);
//     } catch (error) {
//       reject(error);
//     }
//   });
// };

export const getFacilitiyLight = () => {
  let url = `${baseURL}/TelerikReports/GetFacilities`;

  return new Promise(async (resolve, reject) => {
    try {
      const fetch = axios.get(url);
      // const fetch = await makeApiCall({
      //   url: url,
      //   headers: { Authorization: `bearer ${getToken()}` },
      //   method: 'get',
      // });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const sendMailCreateTemplate = (data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const facility = await makeApiCall({
        url: '/EmailNotification/',
        method: 'post',
        headers: { Authorization: `bearer ${getToken()}` },
        data: data,
      });

      return resolve(facility);
    } catch (error) {
      return reject(error);
    }
  });
};

export const getEmailTemplates = () => {
  let url = `${baseURL}/EmailNotificationTemplate`;

  return new Promise(async (resolve, reject) => {
    try {
      //const fetch = axios.get(url);
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const sendPaymentAcknowledgement = (id) => {
  let url = `${baseURL}/Payment/SendPaymentAcknowledgement/${id}`;

  return new Promise(async (resolve, reject) => {
    try {
      //const fetch = axios.get(url);
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getEmailRecipients = (emailCategory, facilityId, moduleId) => {
  let url = `${baseURL}/EmailLog/EmailLogs/?EmailCategory=${emailCategory}&FacilityId=${facilityId}&ModuleId=${moduleId}`;

  return new Promise(async (resolve, reject) => {
    try {
      //const fetch = axios.get(url);
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

// ---- Email log report (Reporting > Activity) ----

// Paged list: { data, total }. start is the 1-based page number, stop the page size.
export const getEmailLogs = async ({
  emailCategory = '',
  search = '',
  facilityId = '',
  emailStatus = '',
  start = 1,
  stop = 25,
} = {}) => {
  // Only send filters that have a value
  const params = new URLSearchParams(
    Object.entries({
      EmailCategory: emailCategory,
      search,
      FacilityId: facilityId,
      EmailStatus: emailStatus,
      start,
      stop,
    }).filter(([, v]) => v !== '' && v !== null && v !== undefined)
  );
  let response;
  try {
    response = await axios.get(`${baseURL}/EmailLog/EmailLogs?${params.toString()}`, {
      headers: { Authorization: `bearer ${getToken()}` },
    });
  } catch (error) {
    // Surface the server's own message rather than "Request failed with status code ..."
    const body = error?.response?.data;
    const desc = body?.result?.status?.desc || body?.status?.desc || body?.message;
    throw new Error(desc || "Couldn't load the email log. Please try again.");
  }
  const payload = response.data;
  const result = payload?.result ?? payload;
  const code = result?.status?.code;
  if (code && !['00', '03'].includes(code)) {
    throw new Error(result?.status?.desc || 'Unable to load email logs');
  }
  const list = Array.isArray(result?.data) ? result.data : [];
  return { data: list, total: result?.total ?? result?.totalCount ?? list.length };
};

// Lookup lists for the email log filters (plain arrays from the report service)
const getTelerikLookup = async (path) => {
  const response = await axios.get(`${baseURL}/TelerikReports/${path}`, {
    headers: { Authorization: `bearer ${getToken()}` },
  });
  const payload = response.data;
  const list = Array.isArray(payload) ? payload : payload?.result?.data || payload?.data || [];
  return list
    .map((item) =>
      typeof item === 'string'
        ? item
        : item?.name || item?.value || item?.emailStatus || item?.emailCategory || item?.text || ''
    )
    .filter(Boolean);
};

export const getEmailLogStatuses = () => getTelerikLookup('GetEmailStatus');

export const getEmailLogCategories = () => getTelerikLookup('GetEmailCategory');

export const getConsultantsV2 = (search, start, stop) => {
  let url = `/Consultant?search=${search}&start=${start}&stop=${stop}`;

  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const approveConsultant = (userId) => {
  let url = `/Consultant/ApproveConsultant/${userId}`;

  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'put',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const deactivateConsultant = (userId) => {
  let url = `/Consultant/DeactivateUser/${userId}`;

  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'put',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const activateConsultant = (userId) => {
  let url = `/Consultant/ActivateUser/${userId}`;

  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'put',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getConsultantV2ByUserId = (userId) => {
  let url = `/Consultant/Id?Id=${userId}`;

  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getConsultantV2Facilities = (userId) => {
  let url = `/Consultant/GetConsultantFacilities/${userId}`;
  console.log(url);

  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getConsultantV2SearchFacilities = () => {
  let url = `/Consultant/SearchFacilities`;
  console.log(url);

  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const consultantRequestToFacility = (data) => {
  let url = `${baseURL}/Consultant/AddConsultantFacility`;

  return new Promise(async (resolve, reject) => {
    try {
      //const fetch = axios.get(url);
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'post',
        data: data,
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getFacilitiyConsultants = (facilityId) => {
  let url = `/Consultant/GetConsultantFacilityInvitesByPersonnel/${facilityId}`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getFacilitiyCertificates = (facilityId) => {
  let url = `/Report/FacilityCertificates/${facilityId}`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const AssignConsultantToFacility = (data) => {
  let url = `/Consultant/AssignConsultantToFacility`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'post',
        data: data,
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getConsultantFacilities = (userId) => {
  let url = `/Consultant/GetConsultantFacilities/${userId}`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const approveConsultantFacilityRequest = (data) => {
  let url = `/Consultant/ApproveFacilityRequest`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'put',
        data: data,
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const consultantAcceptFacilityInvite = (data) => {
  let url = `/Consultant/ApproveFacilityInvite`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'put',
        data: data,
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const consultantScheduleMonitoringDate = (data) => {
  let url = `/Report/SheduleReport`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'post',
        data: data,
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const getRecommendations = (facilityId, quarter, year) => {
  let url = `/ReportRecommendation?facilityId=${facilityId}&reportYear=${year}&reportQuarter=${quarter}`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'get',
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const addRecommendations = (data) => {
  let url = `/ReportRecommendation`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'post',
        data: data,
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const updateRecommendation = (data) => {
  let url = `/ReportRecommendation`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'put',
        data: data,
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};

export const deleteRecommendation = (id) => {
  let url = `/ReportRecommendation/${id}`;
  return new Promise(async (resolve, reject) => {
    try {
      const fetch = await makeApiCall({
        url: url,
        headers: { Authorization: `bearer ${getToken()}` },
        method: 'delete',
        data: data,
      });
      return resolve(fetch);
    } catch (error) {
      reject(error);
    }
  });
};
